import {
  PointsMaterial,
  PointsMaterialParameters,
  ShaderMaterial,
  ShaderMaterialParameters,
  Texture,
  Vector2,
  Vector3,
  WebGLProgramParametersWithUniforms,
} from "three";
const shaderMaterial = new ShaderMaterial();

export class PointCloudMaterial extends PointsMaterial {
  private _shader: Partial<WebGLProgramParametersWithUniforms>;
  private _maxIterations: number;
  private _perspectiveCameraPosition: Vector3 = new Vector3();
  private _rootLength: number;
  private _tileIndex: number;

  // DXF spatial-texture blend (shared material for all tiles → set once).
  public dxfEnabled = false;
  public dxfTex: Texture | null = null;
  public dxfRadius = 2;
  public dxfHardness = 0;
  public dxfOpacity = 1;
  public dxfMaxDist = 10;
  public dxfOffset = new Vector2();
  public dxfTexMin = new Vector2();
  public dxfTexSpan = new Vector2(1, 1);

  public get shader(): Partial<WebGLProgramParametersWithUniforms> {
    return this._shader;
  }

  /** Push the current DXF values into the compiled shader's uniforms (O(1), for slider drag). */
  public applyDxfUniforms() {
    const u = this._shader?.uniforms;
    if (!u || !u["u_dxfEnabled"]) return;
    u["u_dxfEnabled"].value = this.dxfEnabled ? 1.0 : 0.0;
    u["u_dxfTex"].value = this.dxfTex;
    u["u_dxfRadius"].value = this.dxfRadius;
    u["u_dxfHardness"].value = this.dxfHardness;
    u["u_dxfOpacity"].value = this.dxfOpacity;
    u["u_dxfMaxDist"].value = this.dxfMaxDist;
    u["u_dxfOffset"].value = this.dxfOffset;
    u["u_dxfTexMin"].value = this.dxfTexMin;
    u["u_dxfTexSpan"].value = this.dxfTexSpan;
  }

  public set maxIterations(value: number) {
    this._maxIterations = value;
  }

  public get maxIterations() {
    return this._maxIterations;
  }

  public setCameraInfo(rootLength: number, perspectiveCameraPosition: Vector3) {
    this._rootLength = rootLength;
    this._perspectiveCameraPosition.copy(perspectiveCameraPosition);
  }

  constructor(
    parameters?: PointsMaterialParameters,
    materialType?: string,
    tileIndex?: number
  ) {
    super(parameters);
    this._maxIterations = 1;
    this._tileIndex = tileIndex;
    this._shader = {
      uniforms: {
        shapeMidPoints: { value: [] },
        shapeMidNormals: { value: [] },
        triangleIndices: { value: [] },
        shapeCounts: { value: [] },
        highlightColor: { value: new Vector3(1, 0, 0) },
        isSelectInsides: { value: [] },
        isDeleteds: { value: [] },
        totalNumberOfShapes: { value: 0 },
      },
      vertexShader: "",
      fragmentShader: "",
    };

    this.onBeforeCompile = (shader): void => {
      this._shader = shader;
      shader.uniforms["tileIndex"] = {
        value: this._tileIndex >= 0 ? this._tileIndex / 255.0 : 1.0,
      };

      shader.uniforms["perspectiveCameraPosition"] = {
        value: this._perspectiveCameraPosition
          ? this._perspectiveCameraPosition
          : new Vector3(0, 0, 0),
      };

      shader.uniforms["rootLength"] = {
        value: this._rootLength ? this._rootLength : 1.0,
      };

      shader.uniforms["pointScale"] = { value: 3.0 };

      shader.uniforms["shapeMidPoints"] = { value: [0.0] };
      shader.uniforms["shapeMidNormals"] = { value: [0.0] };
      shader.uniforms["triangleIndices"] = { value: [0] };
      shader.uniforms["isSelectInsides"] = { value: [false] };
      shader.uniforms["isDeleteds"] = { value: [false] };
      shader.uniforms["shapeCounts"] = { value: [0] };
      shader.uniforms["totalNumberOfShapes"] = { value: 0 };
      shader.uniforms["highlightColor"] = { value: new Vector3(1, 0, 0) };
      shader.uniforms["highlightColor"] = { value: new Vector3(1, 0, 0) };

      // DXF spatial-texture blend uniforms (seeded from fields → updated at runtime via applyDxfUniforms)
      shader.uniforms["u_dxfEnabled"] = { value: this.dxfEnabled ? 1.0 : 0.0 };
      shader.uniforms["u_dxfTex"] = { value: this.dxfTex };
      shader.uniforms["u_dxfRadius"] = { value: this.dxfRadius };
      shader.uniforms["u_dxfHardness"] = { value: this.dxfHardness };
      shader.uniforms["u_dxfOpacity"] = { value: this.dxfOpacity };
      shader.uniforms["u_dxfMaxDist"] = { value: this.dxfMaxDist };
      shader.uniforms["u_dxfOffset"] = { value: this.dxfOffset };
      shader.uniforms["u_dxfTexMin"] = { value: this.dxfTexMin };
      shader.uniforms["u_dxfTexSpan"] = { value: this.dxfTexSpan };
      shader.vertexShader =
        `
    const int MAX_ITERATIONS = ${this._maxIterations};
    

    uniform vec3 perspectiveCameraPosition;
    uniform float rootLength;
    uniform float pointScale;
    uniform float shapeMidPoints[MAX_ITERATIONS * 9];
    uniform float shapeMidNormals[MAX_ITERATIONS * 9];
    uniform int triangleIndices[MAX_ITERATIONS];
    uniform bool isSelectInsides[MAX_ITERATIONS];
    uniform bool isDeleteds[MAX_ITERATIONS];
    uniform int shapeCounts[MAX_ITERATIONS];
    uniform int totalNumberOfShapes;
    uniform vec3 highlightColor;

    attribute vec3 indices;

    varying float pointOpacity;

    // DXF spatial-texture blend
    uniform float u_dxfEnabled;
    uniform vec2 u_dxfOffset;
    uniform vec2 u_dxfTexMin;
    uniform vec2 u_dxfTexSpan;
    flat varying vec2 vDxfUV;

    // #define USE_COLOR_ALPHA
    #define color_type_rgb

    vec3 getColor(){
        vec3 c;
        
        #ifdef color_type_rgb
            c = color;
        #elif defined color_type_indices
            c = indices;
        #endif

        return c;
    }

    bool isPointInsidePlane(vec3 n, vec3 p, vec3 point)
    {
    float constant = - dot(n, p);
    float ndistToPoint = -(dot(n,point) + constant);
    vec3 projectedPoint = point.xyz + (n.xyz * ndistToPoint);
    vec3 dir = point - projectedPoint;
    if(dot(dir,n) < 0.0)
        return false;
    return true;
    }

    bool isPointInsideTriangle(vec3 p1, vec3 n1, vec3 p2, vec3 n2, vec3 p3, vec3 n3, vec3 point){
        bool res = isPointInsidePlane(n1,p1,point);
        if(res){
            res = isPointInsidePlane(n2,p2,point);
        }
        if(res){
            res = isPointInsidePlane(n3,p3,point);
        }
        return res;
    }
            ` + shader.vertexShader;

      shader.vertexShader = shader.vertexShader.replace(
        "#include <begin_vertex>",
        `
        vColor = getColor();

        pointOpacity = 1.0;
        bool isPointDiscarded = false;   
        bool isPointSelected= false; 
        int accumulatedTriangleCount = 0;  
            
        for(int shapeIndex=0; shapeIndex< MAX_ITERATIONS; shapeIndex += 1){
        if(shapeIndex >= totalNumberOfShapes) break;  
        bool isInsideShape = false;
        if(shapeIndex - 1 >= 0){
            accumulatedTriangleCount += shapeCounts[shapeIndex - 1];
        }
        for(int j =0; j< MAX_ITERATIONS; j += 1){
            int shapeCount = shapeCounts[shapeIndex];
            if( j >= shapeCount) break;
            int i = accumulatedTriangleCount + j;
            vec3 p1 = vec3(shapeMidPoints[triangleIndices[i * 3] * 3], shapeMidPoints[triangleIndices[i * 3] * 3 + 1], shapeMidPoints[triangleIndices[i * 3] * 3 + 2]);
            vec3 n1 = vec3(shapeMidNormals[triangleIndices[i * 3] * 3], shapeMidNormals[triangleIndices[i * 3] * 3 + 1], shapeMidNormals[triangleIndices[i * 3] * 3 + 2]);

            vec3 p2 = vec3(shapeMidPoints[triangleIndices[i * 3 + 1]* 3 ], shapeMidPoints[triangleIndices[i * 3 + 1]* 3 + 1], shapeMidPoints[triangleIndices[i * 3 + 1]*3 +2]);
            vec3 n2 = vec3(shapeMidNormals[triangleIndices[i * 3 + 1]* 3 ], shapeMidNormals[triangleIndices[i * 3 + 1]* 3 + 1], shapeMidNormals[triangleIndices[i * 3 + 1]*3 +2]);

            vec3 p3 = vec3(shapeMidPoints[triangleIndices[i * 3 + 2]* 3 ], shapeMidPoints[triangleIndices[i * 3 + 2]* 3 + 1], shapeMidPoints[triangleIndices[i * 3 + 2]*3 +2]);
            vec3 n3 = vec3(shapeMidNormals[triangleIndices[i * 3 + 2]* 3 ], shapeMidNormals[triangleIndices[i * 3 + 2]* 3 + 1], shapeMidNormals[triangleIndices[i * 3 + 2]*3 +2]);
            
            bool pointInsideTriangle = isPointInsideTriangle(p1,n1,p2,n2,p3,n3, position);
            if(pointInsideTriangle){
                isInsideShape = true;
                break;
            }
        }        

        if( isSelectInsides[shapeIndex] == isInsideShape){
            if(isDeleteds[shapeIndex]){
                isPointDiscarded = true;
            }
            else{
                isPointSelected = true;
            }
            break;
        }
        }      
        if(isPointDiscarded){
            pointOpacity = 0.0; 
        }
        if(isPointSelected){
            vColor = highlightColor;
        }

        #include <begin_vertex>   
    `
      );

      shader.vertexShader = shader.vertexShader.replace(
        "gl_PointSize = size;",
        `
        bool isPerspective = isPerspectiveMatrix( projectionMatrix );

        if(isPerspective){
            float dist = distance(position, perspectiveCameraPosition);
            float clamped = clamp((dist / rootLength), 0.0, 1.0);
            float proximity = 1.0 - clamped;
            float scaled = proximity * pointScale;
            gl_PointSize = scaled * size;
        }
        else{
            gl_PointSize = size;
        }
        `
      );

       shader.vertexShader = shader.vertexShader.replace(
        '#include <fog_vertex>',
        `
        #include <fog_vertex>
        // DXF: spatial UV from the point position (East, -North) → spatial texture
        {
          float east = position.x - u_dxfOffset.x;
          float mNorth = u_dxfOffset.y - position.y;
          vDxfUV = vec2(
            (east - u_dxfTexMin.x) / u_dxfTexSpan.x,
            (mNorth - u_dxfTexMin.y) / u_dxfTexSpan.y
          );
        }
        `
      );


      // shader.vertexShader = shader.vertexShader.replace(
      //     '#include <fog_vertex>',
      //     `
      //     #include <fog_vertex>
      //     pointOpacity = 1.0;
      //     if(isHighlightActive){
      //     if(!(isPointInside ^^ isSelectInsides)){
      //         pointOpacity = 0.0;
      //     }
      //     }
      //     `);

      shader.fragmentShader =
        `
    #define color_type_rgb;
    varying float pointOpacity;
    uniform float tileIndex;
       // DXF GPU blend
    uniform float u_dxfEnabled;
    uniform sampler2D u_dxfTex;
    uniform float u_dxfRadius;
    uniform float u_dxfHardness;
    uniform float u_dxfOpacity;
    uniform float u_dxfMaxDist;
    flat varying vec2 vDxfUV;
    ` + shader.fragmentShader;

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <logdepthbuf_fragment>",
        `
        if(pointOpacity == 0.0){
            discard;
        }

        #include <logdepthbuf_fragment> 
        `
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <premultiplied_alpha_fragment>",
        `
        #include <premultiplied_alpha_fragment>

        // DXF spatial-texture color blend (GPU)
        if (u_dxfEnabled > 0.5) {
          // skip points outside the texture (UV out of [0,1]) → no color
          if (vDxfUV.x >= 0.0 && vDxfUV.x <= 1.0 && vDxfUV.y >= 0.0 && vDxfUV.y <= 1.0) {
            vec4 dxfData = texture2D(u_dxfTex, vDxfUV);
            float a = dxfData.a * 255.0;
            if (a < 254.5) {
              float dist = (a / 254.0) * u_dxfMaxDist;
              float blend = smoothstep(u_dxfHardness * u_dxfRadius, u_dxfRadius, dist);
              blend = (1.0 - blend) * u_dxfOpacity;
              gl_FragColor.rgb = mix(gl_FragColor.rgb, dxfData.rgb, blend);
            }
          }
        }

        #ifdef color_type_indices
            gl_FragColor = vec4(vColor, tileIndex);
        #endif
        `
      );

      shader.vertexShader = shader.vertexShader.replace(
        "#define color_type_rgb",
        `#define color_type_${materialType}`
      );
      shader.vertexShader = shader.vertexShader.replace(
        "#define color_type_indices",
        `#define color_type_${materialType}`
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        "#define color_type_rgb",
        `#define color_type_${materialType}`
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        "#define color_type_indices",
        `#define color_type_${materialType}`
      );

      // console.log(shader.vertexShader);
      // console.log(shader.fragmentShader);
    };
  }
}

// #include <premultiplied_alpha_fragment>
//     gl_FragColor.a  = pointOpacity;

// shader.vertexShader = shader.vertexShader.replace(
//     '#include <fog_vertex>',
//     `
//     #include <fog_vertex>
//     if(isHighlightActive){
//     if(!(isPointInside ^^ isSelectInsides)){
//         vColor = vec4( vColor.xyz,0.0);
//     }
//     }
//     `);
// };

// if(isSelectInsides[shapeIndex] && isInsideShape){
//     isInside =  true ;
//     if(isDeleteds[shapeIndex]){
//         isInsideOfDeletedShape = true;
//     }
//     break;
// }

// if(!(isSelectInsides[shapeIndex] || isInsideShape)){
//     isOutside =  true ;
//     if(isDeleteds[shapeIndex]){
//         isOutSideOfDeletedShape = true;
//     }
//     break;
// }

// if(isOutside ){
//     if(isOutSideOfDeletedShape){
//         pointOpacity = 0.0;
//     }
//     else{
//         vColor = highlightColor;
//     }

// }
// if(isInside){
//     if(isInsideOfDeletedShape){
//         pointOpacity = 0.0;
//     }
//     else{
//         vColor = highlightColor;
//     }
// }
